/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getUserId } from "Models/User";
import { Chemicals, Cotton, Flower, Latex, Pollen } from "assets/allergies";
import { Button, Card, Input, Progress, Stack } from "components";
import useCompanion from "hooks/useCompanion";
import useTryCatch from "hooks/useTryCatch";
import { updatePreferences } from "lib/preferencesApi";
import toast from "react-hot-toast";
import { useEffect, useRef } from "react";
import {
  appendOtherOptions,
  safeAppend,
} from "pages/Dashboard/GuestDetailsPage/GuestPreferencesPage";
import { useParams } from "react-router-dom";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useUser } from "state/UserContext";
import { PageLayout } from "templates";
import useGeneralAllergies, {
  GENERAL_ALLERGIES_OPTIONS,
} from "./useGeneralAllergies";
import { usePrefNavigator } from "state/PrefNavigatorContext";
import usePreviousRoute from "utils/usePreviousRoute";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

const GeneralAllergies = () => {
  const { t } = useTranslation();
  const { userData, setLoading, isLoading } = useUser();
  const { userId } = useParams();
  const [companion, setCompanion] = useCompanion(userId);
  const { navigateWithEvent, eventId } = useEventRouting();
  const {
    customAllergy,
    handleOnChange,
    formData,
    setCustomAllergy,
    hasChemicalAllergies,
    chemicalAllergies,
    setChemicalAllergies,
  } = useGeneralAllergies(companion);

  const goBack = usePreviousRoute();

  const {
    progressBarValues,
    getNextStepName,
    getPreviousStepName,
    setCurrentStep,
  } = usePrefNavigator();

  useEffect(() => {
    setCurrentStep("generalAllergies");
  }, []);

  const onContinue = () => {
    const nextStepName = getNextStepName();
    if (nextStepName && ROUTES.choices[nextStepName]) {
      const routePath = ROUTES.choices[nextStepName](userId);
      navigateWithEvent(routePath);
    } else {
      return null;
    }
  };

  const onGoBack = () => {
    pushToDataLayer({
      event: "preferences_back",
      screen_name: "general allergies",
      journey_step: "general allergies",
    });
    const previousStepName = getPreviousStepName();
    if (previousStepName == null) {
      navigateWithEvent(ROUTES.choices.base(userId));
      return;
    }
    goBack();
  };

  const onSkip = async () => {
    pushToDataLayer({
      event: "skip_general_allergies",
      screen_name: "general allergies",
      journey_step: "general allergies",
    });
    const nextStepName = getNextStepName();
    if (nextStepName == null) {
      navigateWithEvent(ROUTES.onboard.done);
    } else {
      onContinue();
    }
  };

  const handleOnSubmit = useTryCatch(async (e) => {
    e.preventDefault();
    pushToDataLayer({
      event: "next_general_allergies",
      screen_name: "general allergies",
      journey_step: "general allergies",
    });
    // Input doesn't forward `required` to the DOM, so validate manually
    if (
      (hasChemicalAllergies && !chemicalAllergies?.trim()) ||
      (formData.other.state === "success" && !customAllergy.trim())
    ) {
      return toast.error(t("common.messages.requiredField"));
    }
    let result = "";
    Object.values(formData).forEach(({ state, value }, i) => {
      if (state === "success" && value !== "") {
        if (value !== 0) {
          result = safeAppend(result, value, ",");
        }
      }
      return result;
    });
    if (customAllergy !== "") {
      result = appendOtherOptions(result, customAllergy);
    }
    if (result.length === 0) {
      result = "No Chemical Allergies";
    }

    const id = getUserId(companion) || userId;
    await updatePreferences(
      id,
      {
        isCompanion: id !== getUserId(userData),
        general_allergies: result,
        chemical_allergies: chemicalAllergies,
      },
      eventId,
    );
    setLoading(false);

    setCompanion({
      ...companion,
      general_allergies: result,
      chemical_allergies: chemicalAllergies,
    });
    onContinue();
  }, setLoading);

  const generalAllergiesIconsMapper = (name) =>
    ({
      latex: Latex,
      chemicals: Chemicals,
      flowers: Flower,
      cotton: Cotton,
      pollen: Pollen,
      other: null,
    })[name];

  const formRef = useRef(null);

  const isInitial = GENERAL_ALLERGIES_OPTIONS.every(
    (key) => formData?.[key]?.state === "initial",
  );
  return (
    <PageLayout
      title={t("generalAllergies.title")}
      note={t("generalAllergies.description")}
      hasPolicy={false}
      hasGoBack
      goBackMethod={onGoBack}
      hasProgressBar
      progressBarValues={progressBarValues}
    >
      <Stack component="article" className="mt-10 max-w-[400px] mx-auto">
        <form
          ref={formRef}
          onSubmit={handleOnSubmit}
          className="justify-between sm:justify-center min-h-full"
        >
          <Stack>
            <div className="grid grid-cols-3 gap-2">
              {GENERAL_ALLERGIES_OPTIONS.map((key) => (
                <Card
                  title={t(`generalAllergies.${key}`)}
                  key={key}
                  variant="allergy"
                  media={generalAllergiesIconsMapper(key)}
                  state={formData[key]?.state}
                  onClick={
                    key === "other"
                      ? () => handleOnChange("other")
                      : () => handleOnChange(key)
                  }
                />
              ))}
            </div>
            {hasChemicalAllergies && (
              <Stack className="my-2">
                <Input
                  id="chemicalAllergies"
                  placeholder={t("generalAllergies.chemicalsPlaceholder")}
                  label={t("generalAllergies.chemicalsSelectionPrompt")}
                  required
                  type="text"
                  value={chemicalAllergies}
                  className="mt-5"
                  onChange={(e) => {
                    setChemicalAllergies(e.target.value);
                  }}
                />
              </Stack>
            )}
            {formData.other.state === "success" && (
              <Stack>
                <Input
                  placeholder={t("generalAllergies.otherPlaceholder")}
                  label={t("generalAllergies.specialConcernsOther")}
                  id="other"
                  name="other"
                  type="text"
                  value={customAllergy}
                  required
                  className="mt-5"
                  onChange={(e) => {
                    setCustomAllergy(e.target.value);
                  }}
                />
              </Stack>
            )}
          </Stack>
          <Stack className="mt-8">
            <div className="flex sm:hidden gap-x-0.5">
              {progressBarValues.map((value, index) => (
                <Progress key={index} value={value} />
              ))}
            </div>
            <div className="mt-2 w-full">
              <Button
                variant="primary"
                onClick={
                  isInitial ? onSkip : () => formRef.current?.requestSubmit()
                }
                fullWidth
                isLoading={isLoading}
              >
                {isInitial
                  ? t("generalAllergies.noAllergiesOptionLabel")
                  : t("generalAllergies.continueButtonLabel")}
              </Button>
            </div>
          </Stack>
        </form>
      </Stack>
    </PageLayout>
  );
};
export default GeneralAllergies;
